import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosHttp from "services/axiosHttp";
import { message } from "antd";

export const initialState = {
	loading: false,
	error: null,
	outlets: [],
	currentOutlet: null,
	total: 0,
	currentPage: 1,
	territoryOutlets: {}, // { [territoryId]: { docs: [], total: 0, loading: false } }
};

export const createOutlet = createAsyncThunk("outlet/createOutlet", async (outletData, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post("outlet-records/create", outletData);
		message.success("Outlet created successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to create outlet");
		return rejectWithValue(error.response?.data || error.message);
	}
});

export const updateOutlet = createAsyncThunk("outlet/updateOutlet", async ({ outletId, data }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.patch(`outlet-records/update?id=${outletId}`, data);
		message.success("Outlet updated successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to update outlet");
		return rejectWithValue(error.response?.data || error.message);
	}
});

export const deleteOutlet = createAsyncThunk("outlet/deleteOutlet", async (outletId, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`outlet-records/delete/${outletId}`);
		message.success("Outlet deleted successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to delete outlet");
		return rejectWithValue(error.response?.data || error.message);
	}
});

export const uploadOutlets = createAsyncThunk("outlet/uploadOutlets", async ({ file, territoryId }, { rejectWithValue }) => {
	try {
		const formData = new FormData();
		formData.append("file", file);

		const response = await axiosHttp.post(`outlet-records/upload?territory_id=${territoryId}`, formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});

		message.success(`Successfully uploaded ${response.data.data?.success_count || 0} outlets!`);
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to upload outlets");
		return rejectWithValue(error.response?.data || error.message);
	}
});

export const fetchOutletsByTerritory = createAsyncThunk("outlet/fetchOutletsByTerritory", async ({ territoryId, filters = {} }, { rejectWithValue }) => {
	try {
		const params = new URLSearchParams();
		if (filters.keyword) params.append("keyword", filters.keyword);
		if (filters.kyc_completed !== undefined && filters.kyc_completed !== null) params.append("kyc_completed", filters.kyc_completed);
		if (filters.status_id !== undefined && filters.status_id !== null) params.append("status_id", filters.status_id);
		const response = await axiosHttp.get(`outlet-records/by-territory/${territoryId}?${params}`);
		return { territoryId, data: response.data.data };
	} catch (error) {
		return rejectWithValue(error.response?.data || error.message);
	}
});

export const transferOutlet = createAsyncThunk("outlet/transferOutlet", async ({ outlet_id, territory_id }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post("outlet-records/transfer", { outlet_id, territory_id });
		message.success("Outlet transferred successfully!");
		return response.data;
	} catch (error) {
		message.error(error.response?.data?.message || "Failed to transfer outlet");
		return rejectWithValue(error.response?.data || error.message);
	}
});

const outletSlice = createSlice({
	name: "outlet",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		clearCurrentOutlet: (state) => {
			state.currentOutlet = null;
		},
		setCurrentPage: (state, action) => {
			state.currentPage = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(createOutlet.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createOutlet.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(createOutlet.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(updateOutlet.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateOutlet.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(updateOutlet.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(deleteOutlet.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteOutlet.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(deleteOutlet.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(uploadOutlets.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(uploadOutlets.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(uploadOutlets.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(transferOutlet.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(transferOutlet.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(transferOutlet.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});

		builder
			.addCase(fetchOutletsByTerritory.pending, (state, action) => {
				const tid = action.meta.arg.territoryId;
				state.territoryOutlets[tid] = { ...(state.territoryOutlets[tid] || {}), loading: true };
			})
			.addCase(fetchOutletsByTerritory.fulfilled, (state, action) => {
				const { territoryId, data } = action.payload;
				state.territoryOutlets[territoryId] = {
					loading: false,
					docs: data?.docs || [],
					total: data?.total || 0,
				};
			})
			.addCase(fetchOutletsByTerritory.rejected, (state, action) => {
				const tid = action.meta.arg.territoryId;
				state.territoryOutlets[tid] = { ...(state.territoryOutlets[tid] || {}), loading: false };
			});
	},
});

export const { clearError, clearCurrentOutlet, setCurrentPage } = outletSlice.actions;

export default outletSlice.reducer;
